import React, { useState } from "react";
import { Navigate } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { userService } from "../../_services";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Eye, EyeOff } from 'lucide-react';

const formSchema = z.object({
  UserName: z.string()
    .min(5, { message: "Username must be at least 5 characters long" })
    .refine((val) => !val.includes(' '), { message: "Username does not allow space" })
    .refine((val) => !/^[0-9]+$/.test(val), { message: "This Username not allowed" }),
  Password: z.string().min(6, { message: "Password must be at least 6 characters long" }),
  ConfirmPassword: z.string(),
  terms: z.boolean().refine((val) => val === true, { message: "You must accept the terms and conditions" })
}).refine((data) => data.Password === data.ConfirmPassword, {
  message: "Password & Confirm Password must match",
  path: ["ConfirmPassword"],
});

const SignUp_second_step_New = ({ isSecUser }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [signupStep, setSignupStep] = useState('2nd');

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            UserName: '',
            Password: '',
            ConfirmPassword: '',
            terms: false,
        },
    });

    // Check if user is already logged in
    if (!userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate to="/Affiliate_Url" replace />;
    }

    // If signup is complete, show success message
    if (signupStep === 'complete') {
        return <Navigate to="/Account/Activate" replace />;
    }

    const createAccount = async (values) => {
        try {
            const step1Data = JSON.parse(localStorage.getItem('signup_step1') || '{}');
            const fname = step1Data.Fname || '';
            const lname = step1Data.Lname || '';
            const phone = step1Data.Phone || '';
            const email = step1Data.Email || '';
            const signupLink = localStorage.getItem('ReferralURL') || '';

            const apidata = await userService.Signup(
                fname,
                lname,
                phone,
                email,
                values.UserName,
                values.Password,
                signupLink,
                ''
            );

            if (apidata.length > 0) {
                if (apidata[0].code === "-1") {
                    form.setError('ConfirmPassword', { message: apidata[0].message });
                    setIsLoading(false);
                } else {
                    userService.logout();
                    setSignupStep('complete');
                }
            }
        }
        catch (error) {
            console.error('Signup error:', error);
            form.setError('ConfirmPassword', { message: 'Error! Please try later' });
            setIsLoading(false);
        }
    };

    const createAccountSecLink = async (values) => {
        try {
            const step1Data = JSON.parse(localStorage.getItem('signup_step1') || '{}');
            const fname = step1Data.Fname || '';
            const lname = step1Data.Lname || '';
            const phone = step1Data.Phone || '';
            const email = step1Data.Email || '';
            const signupLink = localStorage.getItem('ReferralURL') || '';

            const apidata = await userService.Signup_secLink(
                fname,
                lname,
                phone,
                email,
                values.UserName,
                values.Password,
                signupLink,
                ''
            );

            if (apidata.length > 0) {
                if (apidata[0].code === "-1") {
                    form.setError('ConfirmPassword', { message: apidata[0].message });
                    setIsLoading(false);
                } else {
                    userService.logout();
                    setSignupStep('complete');
                }
            }
        }
        catch (error) {
            console.error('SecLink signup error:', error);
            form.setError('ConfirmPassword', { message: 'Error! Please try later' });
            setIsLoading(false);
        }
    };

    const validateUserData = async (values) => {
        try {
            const step1Data = JSON.parse(localStorage.getItem('signup_step1') || '{}');
            const phone = step1Data.Phone || '';
            const email = step1Data.Email || '';

            const apidata = await userService.CheckAvailability(phone, email, values.UserName, true);
            
            if (apidata.length > 0) {
                if (apidata[0].code === "-1") {
                    form.setError('UserName', { message: apidata[0].message });
                    setIsLoading(false);
                } else {
                    // Proceed to create account
                    await createAccount(values);
                }
            }
        }
        catch (error) {
            console.error('User validation error:', error);
            setIsLoading(false);
        }
    };

    const validateUserDataSecLink = async (values) => {
        try {
            const step1Data = JSON.parse(localStorage.getItem('signup_step1') || '{}');
            const phone = step1Data.Phone || '';
            const email = step1Data.Email || '';

            const apidata = await userService.CheckAvailability(phone, email, values.UserName, true);
            
            if (apidata.length > 0) {
                if (apidata[0].code === "-1") {
                    form.setError('UserName', { message: apidata[0].message });
                    setIsLoading(false);
                } else {
                    // Proceed to create account
                    await createAccountSecLink(values);
                }
            }
        }
        catch (error) {
            console.error('SecLink validation error:', error);
            setIsLoading(false);
        }
    };

    const onSubmit = async (values) => {
        setIsLoading(true);
        
        try {
            // Validate username first
            const usernameApiData = await userService.GetUserNameFilter(values.UserName);
            
            if (usernameApiData.length > 0) {
                if (usernameApiData[0].code !== '0') {
                    form.setError('UserName', { message: usernameApiData[0].message });
                    setIsLoading(false);
                    return;
                }
            }

            // Trim username of leading spaces
            const trimmedUsername = values.UserName.replace(/^\s+/, '');
            
            // Validate that username is not all numbers
            if (/^[0-9]+$/.test(trimmedUsername)) {
                form.setError('UserName', { message: 'This Username not allowed' });
                setIsLoading(false);
                return;
            }

            // Update the form value with trimmed username
            form.setValue('UserName', trimmedUsername);
            values.UserName = trimmedUsername;

            // Validate user data and create account
            if (isSecUser) {
                await validateUserDataSecLink(values);
            } else {
                await validateUserData(values);
            }
        }
        catch (error) {
            console.error('Form submission error:', error);
            setIsLoading(false);
        }
    };

    return (
        <div className="ps-0 max-w-md">
            <h2 className="text-3xl font-bold text-foreground mb-6">
                Complete Your Registration
            </h2>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                        control={form.control}
                        name="UserName"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <div className="relative">
                                        <Input 
                                            {...field} 
                                            disabled={isLoading}
                                            placeholder=" "
                                            className="peer h-12"
                                            onChange={(e) => {
                                                // Trim leading spaces
                                                const value = e.target.value.replace(/^\s+/, '');
                                                field.onChange(value);
                                            }}
                                        />
                                        <FormLabel className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                            Username
                                        </FormLabel>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="Password"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <div className="relative">
                                        <Input 
                                            {...field} 
                                            type={showPassword ? "text" : "password"}
                                            disabled={isLoading}
                                            placeholder=" "
                                            className="peer pr-10 h-12"
                                        />
                                        <FormLabel className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                            Password
                                        </FormLabel>
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="ConfirmPassword"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <div className="relative">
                                        <Input 
                                            {...field} 
                                            type={showConfirmPassword ? "text" : "password"}
                                            disabled={isLoading}
                                            placeholder=" "
                                            className="peer pr-10 h-12"
                                        />
                                        <FormLabel className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                            Confirm Password
                                        </FormLabel>
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
                                        >
                                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="terms"
                        render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                <FormControl>
                                    <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                        disabled={isLoading}
                                    />
                                </FormControl>
                                <div className="space-y-1 leading-none">
                                    <FormLabel className="text-sm font-normal">
                                        I accept the{' '}
                                        <a href="/terms" target="_blank" className="text-accent hover:underline">
                                            terms and conditions
                                        </a>
                                    </FormLabel>
                                    <FormMessage />
                                </div>
                            </FormItem>
                        )}
                    />

                    <Button type="submit" className="bg-accent hover:bg-accent/90 text-white w-auto flex align-center justify-center" size="lg" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Loader2 className="mr-0 h-4 w-4 animate-spin" />
                                Creating Account
                            </>
                        ) : (
                            'Complete Signup'
                        )}
                    </Button>
                </form>
            </Form>

            <div className="mt-6 text-left space-y-2">
                <p className="text-md text-foreground opacity-70">
                    Already have an account?{' '}
                    <a href="/login" className="text-accent hover:underline font-medium">
                        Sign In
                    </a>
                </p>
            </div>
        </div>
    );
};

export default SignUp_second_step_New;
